import React from "react";
import { useSelector } from "react-redux";

import { Deal } from "custom-types/Deals";
import { useEventTracker } from "hooks/useEventTracker";
import useGetDealKindLabel from "hooks/useGetDealKindLabel";
import { getUserId } from "redux/selectors/user";
import formatDeals from "utils/formatDeals";
import { MENU_TITLES } from "utils/menuTypeUtils";
import { Action, Category } from "utils/trackEvent";

import DealCard from "components/botanic//DealCard";

import MenuType from "../MenuType";
import NoDealsOrPromos from "../NoDealsOrPromos";

type DealsSectionProps = {
  deals: Deal[];
  dispensaryId: number;
  isDualMenuDispensary: boolean;
  menuType: string;
  setMenuType: (menuType?: string) => void;
};

const DealsSection: React.FC<DealsSectionProps> = ({
  deals,
  dispensaryId,
  isDualMenuDispensary,
  menuType,
  setMenuType,
}) => {
  const {
    todaysDeals,
    todaysMedDeals,
    todaysRecDeals,
    upcomingDeals,
    upcomingMedDeals,
    upcomingRecDeals,
  } = formatDeals(deals);

  const userId = useSelector(getUserId);

  const { publishEvent } = useEventTracker();

  const selectTodaysDeals = isDualMenuDispensary
    ? menuType === MENU_TITLES.med.shortTitle
      ? todaysMedDeals
      : todaysRecDeals
    : todaysDeals;
  const selectUpcomingDeals = isDualMenuDispensary
    ? menuType === MENU_TITLES.med.shortTitle
      ? upcomingMedDeals
      : upcomingRecDeals
    : upcomingDeals;

  const { getDealKindLabel } = useGetDealKindLabel();

  return (
    <div data-testid="deals__today-section" className="mb-xxl">
      <div className="flex items-center justify-between w-full">
        <h2>Deals</h2>
        <MenuType
          isDualMenuDispensary={isDualMenuDispensary}
          setMenuType={setMenuType}
        />
      </div>

      {selectUpcomingDeals.length || selectTodaysDeals.length ? (
        <div className="text-md font-bold mb-lg">
          Order these limited-time deals for pickup (or delivery, where it's
          legal)!
        </div>
      ) : (
        <NoDealsOrPromos type="deals" />
      )}

      {selectTodaysDeals.length > 0 && (
        <>
          <h3 className="text-md mb-xs">Available today</h3>

          <div className="row">
            {selectTodaysDeals.map((deal: Deal) => {
              return (
                <div key={deal.id} className="col md:col-1/2 lg:col-1/3 mb-sm">
                  <DealCard
                    deal={{ ...deal, dealUrl: deal.link }}
                    className="rounded h-full overflow-hidden elevation-low"
                    onClick={() =>
                      publishEvent({
                        action: Action.click,
                        category: Category.dealCards,
                        dealId: deal.id,
                        dispensaryId,
                        label: getDealKindLabel(deal),
                        userId,
                      })
                    }
                    onLoad={() =>
                      publishEvent({
                        action: Action.impression,
                        category: Category.dealCards,
                        dealId: deal.id,
                        dispensaryId,
                        label: getDealKindLabel(deal),
                        nonInteractionEvent: true,
                        userId,
                      })
                    }
                    data-testid="deal-section__deal-card"
                  />
                </div>
              );
            })}
          </div>
        </>
      )}

      {selectUpcomingDeals.length > 0 && (
        <>
          <h3 className="text-green mt-xxl mb-xs">Upcoming deals</h3>

          <div className="row">
            {selectUpcomingDeals.map((deal: Deal) => (
              <div key={deal.id} className="col md:col-1/2 lg:col-1/3 mb-sm">
                <DealCard
                  deal={{ ...deal, dealUrl: deal.link }}
                  onClick={() =>
                    publishEvent({
                      action: Action.click,
                      category: Category.dealCards,
                      dealId: deal.id,
                      dispensaryId,
                      label: getDealKindLabel(deal),
                      userId,
                    })
                  }
                  onLoad={() =>
                    publishEvent({
                      action: Action.impression,
                      category: Category.dealCards,
                      dealId: deal.id,
                      dispensaryId,
                      label: getDealKindLabel(deal),
                      userId,
                    })
                  }
                  className="rounded h-full overflow-hidden elevation-low"
                  data-testid="deal-section__deal-card"
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default DealsSection;
